/**
 * RhwpEditorViewer — @rhwp/editor (iframe 임베드) 기반 한글 편집기 뷰어.
 *
 * 기존 RhwpViewer.js (@rhwp/core 직접 호출) 의 한계를 우회하기 위해 도입:
 *  - @rhwp/core 의 replaceAll/replaceOne 은 body 문단만 스캔, 표 셀 미지원
 *  - 클릭 좌표를 알면서도 검색-기반 치환을 써서 본문 중복 단어 동시 변경
 *  - 빈 셀은 if(!text) return 으로 편집 시작조차 불가
 *
 * 이 컴포넌트는 편집 UX 를 통째로 rhwp-studio iframe 에 위임한다.
 *  - 표 편집 / 서식 / 툴바 / 단축키 모두 자체 지원
 *  - 우리는 컨테이너만 제공하고 fileId 변경 시 loadFile 만 다시 호출
 *
 * 사용 시그니처는 기존 RhwpViewer.js 와 동일: { fileId, filename, onError, onReady }.
 * 단계적 교체를 위해 props 호환성 유지.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createEditor } from '@rhwp/editor';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import { downloadBlobFile } from '../../utils/downloadUtils';
import { auth } from '../../firebase/config';
import { disableRhwpStudioAutosave } from './rhwpAutosavePolicy';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const RHWP_EDITOR_VERSION = '0.8.7';
const RHWP_DOCUMENT_AGENT_ENABLED = false;
const RHWP_HANDSHAKE_TIMEOUT_MS = 5000;
const RHWP_STUDIO_BASE_URL =
  process.env.REACT_APP_RHWP_STUDIO_URL ||
  `/rhwp-studio/${RHWP_EDITOR_VERSION}/`;

// raw-bytes 는 단순 캐시 read 라 매우 빠르게 응답해야 함.
// 첫 로딩 시 Cloud Run cold start / 일시 5xx / 네트워크 흔들림에 대비해 재시도.
//   - maxRetries=3: 총 4 시도 (백오프 3s,6s,12s 의 처음 3개)
//   - timeoutMs=10s: 각 시도별. cold start 도 10s 안에 응답 못하면 인스턴스 이상.
//   - retryOnTimeout=true: 같은 file_id 의 동일 호출은 재시도가 의미 있음.
const RAW_BYTES_FETCH_OPTS = {
  maxRetries: 3,
  timeoutMs: 10_000,
  retryOnTimeout: true,
};

// export-edited-pdf 는 LibreOffice + H2Orestart 변환 (cold ~10s, warm ~3s).
//   - maxRetries=2: 총 3 시도. 변환 비용이 크므로 너무 많이 반복하지 않음.
//   - timeoutMs=60s: 변환 + 다운로드 여유.
//   - retryOnTimeout=false: 60s 기다렸으면 같은 input 으로 재호출 의미 적음.
const EXPORT_PDF_FETCH_OPTS = {
  maxRetries: 2,
  timeoutMs: 60_000,
  retryOnTimeout: false,
};

// Cloud Run 은 32 MiB 넘는 요청 본문을 CORS 헤더 없이 거절해서 브라우저에는 네트워크 오류로만 보인다.
// 헤더 몫을 남기고 보내기 전에 막는다.
export const RHWP_MAX_UPLOAD_BYTES = 32 * 1024 * 1024 - 256 * 1024;
export const RHWP_SAVE_TOO_LARGE_MESSAGE =
  "편집한 파일이 32MB를 넘어서 왓퀴즈에 저장할 수 없어요. '파일 다운로드'로 내 컴퓨터에 받아주세요.";
export const RHWP_PDF_TOO_LARGE_MESSAGE =
  '편집한 파일이 32MB를 넘어서 여기서 PDF로 만들 수 없어요. 한글 파일로 저장한 뒤 한글 프로그램에서 PDF로 저장해주세요.';

async function getAuthHeaders(extraHeaders = {}) {
  const currentUser = auth.currentUser;
  if (!currentUser) return extraHeaders;
  const token = await currentUser.getIdToken();
  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

async function reportHwpExportTelemetry(fileId, payload, telemetryPath = null) {
  if (!fileId || telemetryPath === false) return;
  try {
    const endpoint = telemetryPath || (
      `/api/chat/files/${encodeURIComponent(fileId)}/hwp-export-telemetry`
    );
    await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        keepalive: true,
      }
    );
  } catch (error) {
    // 텔레메트리는 best-effort이며 다운로드 UX를 절대 막지 않는다.
    console.debug('[RhwpEditorViewer] export telemetry 전송 생략:', error);
  }
}

// 한글 파일 이벤트(/events/hwp-*) 결과물은 익명 공개 raw-bytes 경로를 쓴다.
// 접두사 고정 + 안전한 세그먼트만 허용해 임의 URL 주입을 막는다.
const HWP_DOC_EVENT_RAW_BYTES_PATH_RE =
  /^\/api\/events\/hwp-doc\/[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+){0,2}$/;
// 공유받은 대화(/share/:id)의 생성 한글 파일. 활성 공유에 든 파일만 서버가 내준다.
const CHAT_SHARE_RAW_BYTES_PATH_RE =
  /^\/api\/chat\/share\/[A-Za-z0-9]{6,32}\/files\/[A-Za-z0-9_-]+\/raw-bytes$/;

export function resolveRhwpRawBytesUrl(fileId, rawBytesPath = null) {
  if (rawBytesPath) {
    const path = String(rawBytesPath);
    if (
      !/^\/api\/chat\/(?:files|artifacts)\/[A-Za-z0-9_-]+\/raw-bytes$/.test(path)
      && !HWP_DOC_EVENT_RAW_BYTES_PATH_RE.test(path)
      && !CHAT_SHARE_RAW_BYTES_PATH_RE.test(path)
    ) {
      throw new Error('문서 편집 주소를 확인하지 못했어요.');
    }
    return `${API_BASE_URL}${path}`;
  }
  return (
    `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/raw-bytes`
  );
}

export function resolveRhwpServerSaveUrl(fileId, serverSavePath = null) {
  if (!serverSavePath) return null;
  const path = String(serverSavePath);
  const expected = `/api/chat/files/${encodeURIComponent(fileId)}/raw-bytes`;
  if (path !== expected) {
    throw new Error('문서 저장 주소를 확인하지 못했어요.');
  }
  return `${API_BASE_URL}${path}`;
}

// ── loadFile 옵션 ─────────────────────────────────────────────────────────
// - suppressDialogs: 로드 후 안내창(HWPX 검증·글꼴) 없이 열기(임베드에서 응답 교착 방지)
// - skipUnsavedGuard: 스튜디오 자체 「저장하지 않은 변경사항」 확인창 생략.
//   직접 편집 중인 문서를 채팅 수정으로 교체할지는 부모(ChatPage → HwpxEditConflictDialog)가
//   flushDirtyState 로 이미 물어본 뒤 loadFile 을 호출한다. 이 옵션이 없으면 스튜디오가
//   iframe 안에 또 한 번 확인창을 띄우고 loadFile 응답이 60초 타임아웃까지 멈춰
//   「새 수정본을 아직 불러오지 못했어요」가 뜬다(2026-09 E2E 에서 재현).
const LOAD_FILE_OPTIONS = Object.freeze({ suppressDialogs: true, skipUnsavedGuard: true });

// 직접 수정 감지 — 편집 동작이 멈추고 이만큼 지나면 export 바이트를 원본과 비교한다.
const EDIT_ACTIVITY_SETTLE_MS = 1500;
const EDIT_ACTIVITY_EVENTS = Object.freeze(['input', 'keydown', 'paste', 'cut', 'drop', 'pointerup']);

// ── createEditor / loadFile 재시도 정책 ───────────────────────────────────
//
// 배경:
//   @rhwp/editor <=0.7.18 은 모든 요청을 10초에 끊었다. 0.8.6부터 transport
//   timeout을 설정할 수 있고 load/export 기본값도 60초로 늘어났다. 왓퀴즈는
//   대형 학교 양식과 cold WASM 초기화를 고려해 60초를 명시한다.
//
// 재시도 정책 (M1 확장 — 사용자 호소 "로딩해도 되니까 좀 남겨두기"):
//   - timeout 류 에러: 동일 요청을 자동 반복하지 않고 사용자가 다시 시도할 수
//     있게 한다. loadFile은 suppressDialogs=true라 안내창 교착을 만들지 않는다.
//   - timeout 외 에러: 백오프 후 재시도. 마지막 시도에서 실패해야 onError 호출.
//
// 백오프 (밀리초) — Cloud Run cold start + wasm 다운로드 대응:
//   - createEditor: [2000, 4000, 8000, 16000] → 최대 4회 재시도 (총 5 시도)
//   - loadFile: [1000, 3000, 6000] → 최대 3회 재시도 (총 4 시도)
const RHWP_REQUEST_TIMEOUT_MS = 60_000;
const CREATE_EDITOR_RETRY_BACKOFFS_MS = [2000, 4000, 8000, 16000];
const LOADFILE_RETRY_BACKOFFS_MS = [1000, 3000, 6000];

export function buildRhwpCreateEditorOptions({ studioUrl } = {}) {
  const options = {
    requestTimeoutMs: RHWP_REQUEST_TIMEOUT_MS,
    handshakeTimeoutMs: RHWP_HANDSHAKE_TIMEOUT_MS,
    studioUrl,
  };
  const renderer = process.env.REACT_APP_RHWP_RENDERER;
  if (renderer) options.renderer = renderer;
  return options;
}

export function readRhwpNotifySavedCapability(editor) {
  try {
    const supports = editor?._transport?.supports;
    if (typeof supports !== 'function') return 'unknown';
    const value = supports.call(editor._transport, 'notify-saved-v1');
    if (value === true) return true;
    if (value === false) return false;
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

export { RHWP_DOCUMENT_AGENT_ENABLED, RHWP_HANDSHAKE_TIMEOUT_MS };

// HWP/HWPX magic 감지 (raw-bytes 응답과 export 결과의 실제 형식을 판별).
//   - HWP v5 : CFB compound file → 0xD0 0xCF 0x11 0xE0 0xA1 0xB1 0x1A 0xE1
//   - HWPX   : ZIP → 0x50 0x4B (PK)
export function detectRhwpDocumentKind(bytes) {
  if (!bytes || bytes.byteLength < 4) return null;
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // ZIP (HWPX)
  if (b[0] === 0x50 && b[1] === 0x4B) return 'hwpx';
  // CFB (HWP v5)
  if (
    b.byteLength >= 8 &&
    b[0] === 0xD0 && b[1] === 0xCF && b[2] === 0x11 && b[3] === 0xE0 &&
    b[4] === 0xA1 && b[5] === 0xB1 && b[6] === 0x1A && b[7] === 0xE1
  ) return 'hwp_v5';
  return null;
}

function _looksLikeHwpDocument(bytes) {
  return detectRhwpDocumentKind(bytes) !== null;
}

function sameExportBytes(current, baseline) {
  return Boolean(baseline)
    && baseline.length === current.length
    && current.every((value, index) => value === baseline[index]);
}

export const RHWP_SERVER_SAVE_FAILED_MESSAGE =
  '작업 내용을 왓퀴즈에 저장하지 못했어요. 인터넷 연결을 확인하고 다시 눌러주세요.';
export const RHWP_SERVER_SAVE_PAGES_CHANGED_MESSAGE =
  "다시 열어 보니 쪽수가 달라져 저장하지 않았어요. 'PDF로 저장'으로 받아 두세요.";
// 토스트가 안내하는 다음 버튼. 하단 바에 이미 있는 버튼이라 토스트에 같은 버튼을 또 두지 않고 테두리로 짚어 준다.
const RHWP_SUGGESTED_ACTION_RING = '0 0 0 3px #BFDBFE';

// next: 'pdf' = 같은 편집본이라 PC로 받아도 똑같이 깨지니 PDF로, 'retry' = 다시 누르면 될 수 있음,
// 'download' = 서버로만 못 보내는 경우.
function serverSaveError(message, reason, next) {
  return Object.assign(new Error(message), { saveFailure: { reason, next } });
}

function serverSaveFailureOf(status, detail) {
  const reason = (detail && typeof detail === 'object'
    ? detail.raw_check || detail.blocked_reason
    : null) || `http_${status}`;
  let next = null;
  if (status === 422) next = reason === 'editor_pages_unknown' ? 'retry' : 'pdf';
  else if (status >= 500 || status === 408 || status === 429) next = 'retry';
  return { reason, next };
}

function _buildRhwpStudioUrl(attempt = 0, resetKey = 0) {
  try {
    const url = new URL(RHWP_STUDIO_BASE_URL, window.location.origin);
    url.searchParams.set('whatquiz_rhwp', RHWP_EDITOR_VERSION);
    if (attempt > 0 || resetKey > 0) {
      url.searchParams.set('reload', `${resetKey}-${attempt}-${Date.now()}`);
    }
    return url.toString();
  } catch {
    const sep = RHWP_STUDIO_BASE_URL.includes('?') ? '&' : '?';
    return `${RHWP_STUDIO_BASE_URL}${sep}whatquiz_rhwp=${RHWP_EDITOR_VERSION}&reload=${resetKey}-${attempt}-${Date.now()}`;
  }
}

function _isRhwpWasmInitError(err) {
  const message = err?.message || String(err || '');
  return /__wbindgen_malloc|wasm-bindgen|WebAssembly|rhwp.*wasm/i.test(message);
}

function _nowMs() {
  return (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();
}

function _sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function _abortError() {
  const error = new Error('요청을 취소했어요.');
  error.name = 'AbortError';
  return error;
}

// ── raw-bytes 프리페치 ─────────────────────────────────────────────────
//
// 과거에는 createEditor(iframe → studio JS 1.7MB → wasm 2.6MB 초기화)가 끝난 뒤에야
// raw-bytes 를 받기 시작해 체인이 완전히 직렬이었다(성공 사례 load_ms 최대 11초).
// fileId/rawBytesPath 가 정해지는 즉시 fetch 를 시작해 wasm 초기화와 겹치게 하고,
// loadFile effect 는 그 promise 를 기다린다. 결과는 { buf, detectedKind } 다.
export function buildRhwpRawBytesPrefetchKey(fileId, rawBytesPath = null, reloadKey = 0) {
  return `${fileId || ''}|${rawBytesPath || ''}|${reloadKey}`;
}

async function fetchRhwpRawBytes({ fileId, rawBytesPath, signal, onRetry }) {
  // raw-bytes fetch — 재시도 포함 (5xx / 네트워크 / 각 시도 10s 타임아웃).
  // 4xx (예: 404 미지원 양식) 는 fetchWithRetry 내부에서 재시도하지 않음.
  const url = resolveRhwpRawBytesUrl(fileId, rawBytesPath);
  const res = await fetchWithRetry(
    url,
    { method: 'GET', headers: await getAuthHeaders(), signal },
    { ...RAW_BYTES_FETCH_OPTS, onRetry }
  );
  if (!res.ok) throw new Error(`문서 로드 실패 (${res.status})`);
  let buf = await res.arrayBuffer();
  if (signal?.aborted) throw _abortError();

  // 응답이 비었거나 magic 깨졌으면 한 번 더 raw-bytes 재요청 (서버 일시 race 보호).
  // 첫 try 가 200 OK 였더라도 빈 바이트나 잘못된 응답을 받으면 wasm 가 silent fail
  // 또는 pageCount=0 으로 빠지므로, 진입 전에 잡는 게 가장 저렴.
  if (!buf || buf.byteLength === 0 || !_looksLikeHwpDocument(buf)) {
    console.warn(
      `[RhwpEditorViewer] raw-bytes 응답이 비정상 (bytes=${buf?.byteLength ?? 0}) — 1회 재요청`
    );
    await _sleep(800);
    if (signal?.aborted) throw _abortError();
    const res2 = await fetchWithRetry(
      url,
      { method: 'GET', headers: await getAuthHeaders(), signal },
      { ...RAW_BYTES_FETCH_OPTS, maxRetries: 1 }
    );
    if (!res2.ok) throw new Error(`문서 재로드 실패 (${res2.status})`);
    buf = await res2.arrayBuffer();
    if (signal?.aborted) throw _abortError();
    if (!buf || buf.byteLength === 0 || !_looksLikeHwpDocument(buf)) {
      throw new Error('서버에서 받은 한글 파일을 확인하지 못했어요. 잠시 후 다시 시도해주세요.');
    }
  }
  const detectedKind = detectRhwpDocumentKind(buf);
  if (!detectedKind) {
    throw new Error('서버에서 받은 한글 파일 형식을 확인하지 못했어요.');
  }
  return { buf, detectedKind };
}

function Overlay({ text, kind = 'info', highlight = null, action = null }) {
  const color = kind === 'error' ? '#EF4444' : '#191F28';
  // action 이 있으면 클릭 가능해야 하므로 pointerEvents 활성화.
  const interactive = !!action;
  return (
    <div
      className="rhwp-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(243,244,246,0.96)',
        zIndex: 2,
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          color,
          fontSize: 14,
          fontWeight: 500,
          textAlign: 'center',
          padding: 20,
          maxWidth: 400,
        }}
      >
        {kind === 'info' && (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '3px solid #E5E8EB',
              borderTopColor: '#3182F6',
              animation: 'rhwp-spin 0.9s linear infinite',
            }}
          />
        )}
        <div>{text}</div>
        {highlight && (
          <div
            style={{
              marginTop: 4,
              background: '#EFF4FF',
              color: '#1B64DA',
              borderRadius: 10,
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.5,
              maxWidth: 320,
            }}
          >
            {highlight}
          </div>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="rhwp-overlay-retry-btn"
            style={{
              marginTop: 8,
              border: 'none',
              borderRadius: 10,
              background: '#3182F6',
              color: '#fff',
              padding: '10px 22px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(49,130,246,0.3)',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2563EB'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#3182F6'; }}
          >
            {action.label}
          </button>
        )}
      </div>
      <style>{'@keyframes rhwp-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// 양식 상단 sticky 안내 strip — 대화(conversation) 단위 dismiss.
// 사용자가 × 누르면 그 conversationId 만 localStorage 에 기억 → 다른
// 대화에서는 다시 노출. 매 대화 시작 1회는 다시 확인 가능 (Context Based).
//
// 저장 구조: { [conversationId]: timestamp } JSON. 7일 이상 된 항목은 자동
// 정리해서 누적 부풀음 방지.
const LOCAL_EDIT_NOTICE_DISMISS_KEY = 'rhwp.localedit.notice.dismissed.v3';
const LOCAL_EDIT_NOTICE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7일

function _readDismissedMap() {
  try {
    const raw = window.localStorage.getItem(LOCAL_EDIT_NOTICE_DISMISS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function _writeDismissedMap(map) {
  try {
    // 만료된 항목 정리 — 무한 누적 방지
    const cutoff = Date.now() - LOCAL_EDIT_NOTICE_TTL_MS;
    const cleaned = {};
    for (const [k, v] of Object.entries(map)) {
      if (typeof v === 'number' && v >= cutoff) cleaned[k] = v;
    }
    window.localStorage.setItem(LOCAL_EDIT_NOTICE_DISMISS_KEY, JSON.stringify(cleaned));
  } catch {}
}

function isLocalEditNoticeDismissed(conversationId) {
  if (!conversationId) return false;
  const map = _readDismissedMap();
  const ts = map[conversationId];
  if (typeof ts !== 'number') return false;
  // TTL 초과면 미dismiss 로 간주 (정리는 write 시점에)
  return Date.now() - ts < LOCAL_EDIT_NOTICE_TTL_MS;
}

function markLocalEditNoticeDismissed(conversationId) {
  if (!conversationId) return;
  const map = _readDismissedMap();
  map[conversationId] = Date.now();
  _writeDismissedMap(map);
}

const RhwpEditorViewer = forwardRef(function RhwpEditorViewer({
  fileId,
  filename,
  previewKind,
  conversationId,
  rawBytesPath = null,
  allowPdfExport = true,
  telemetryPath = null,
  serverSavePath = null,
  serverSaveLabel = '작업 저장',
  localEditNoticeVariant = 'chat',
  // 뷰어 위 레이아웃 안내 { title, body } | null. 문서(fileId)마다 다시 보이고, 닫으면 그 문서에서는 숨긴다.
  layoutNotice = null,
  onError,
  onReady,
  onStage,
  onPotentialEdit,
  onServerSaveStart,
  onServerSave,
  // 작업 저장 실패 알림 { reason } — reason 은 서버 blocked_reason/raw_check 또는 브라우저 단계 이름.
  onServerSaveFailed,
  // 다운로드 결과 알림 { entry: 'editor_hwp'|'editor_pdf', format, ok, failureStage } — 계측은 부모가 한다.
  onDownload,
}, forwardedRef) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  // fileId 가 빠르게 두 번 바뀔 때 stale 응답이 새 응답을 덮어쓰는 race 차단.
  const reqIdRef = useRef(0);
  // ★ [Phase 4] loadFile 성공 이력 — timeout silent 처리 시 "이전 문서가
  //   화면에 stale 로 남은 상황"인지 판별. 성공 이력이 있으면 사용자에게
  //   갱신 실패를 알리고 다시 시도 액션을 제공한다 (조용한 stale 제거).
  const hasLoadedOnceRef = useRef(false);
  // prop fileId가 바뀐 직후 effect가 실행되기 전 한 프레임 동안 이전 문서를 새
  // 파일명으로 저장하는 race를 막는다. 성공적으로 loadFile한 id만 저장 허용.
  const loadedFileIdRef = useRef(null);
  // editor 인스턴스 준비 완료 여부 — loadFile effect 는 이게 true 일 때만 동작.
  const [editorReady, setEditorReady] = useState(false);
  const [loading, setLoading] = useState(true);
  // 로딩 중 사용자에게 보여줄 텍스트. 재시도 phase 에 따라 갱신:
  //   "문서 준비 중이에요" → 첫 시도
  //   "잠시만요, 다시 시도하고 있어요 (n/m)" → 재시도 중
  const [loadingMessage, setLoadingMessage] = useState('문서 준비 중이에요');
  const [error, setError] = useState(null);
  // previewKind는 서버 힌트일 뿐이다. 실제 로드한 바이트의 magic을 저장 경로의
  // 권위로 사용해 HWPX를 HWP로 잘못 변환하거나 확장자를 틀리는 일을 막는다.
  const [loadedDocumentKind, setLoadedDocumentKind] = useState(null);
  // 다운로드 진행 상태 — 사용자에게 시각적 피드백 제공.
  const [downloading, setDownloading] = useState(false);
  // 별도 PDF 다운로드 상태 — hwpx 양식의 "자체 편집까지 PDF 저장" 버튼.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingServer, setSavingServer] = useState(false);
  // 마지막 작업 결과 토스트 (성공/실패) — 3초 후 자동 사라짐.
  const [toast, setToast] = useState(null);

  // 양식 위 안내 카드 — 대화 단위 dismiss.
  // cross-origin iframe 이라 키입력 정확 trigger 불가 → 영구 안내 + 5초
  // 지연 노출. 양식 첫 로드 직후 폭격하지 않고 사용자가 양식을 잠깐 본
  // 다음 자연스럽게 보이게.
  const [showLocalEditNotice, setShowLocalEditNotice] = useState(false);

  useEffect(() => {
    // 'assessment'(평가계획서 작업 저장 안내)에서만 쓴다.
    // 'chat': 채팅 학습지 패널은 안내가 2단 레이아웃 안내와 겹쳐 두 장이 쌓이므로 「여기서 수정한 내용은 먼저 저장」 카드를 없앴다.
    // 'none': 대화(conversation)가 없는 읽기 전용 임베드(이벤트 결과 뷰어).
    if (localEditNoticeVariant !== 'assessment' || isLocalEditNoticeDismissed(conversationId)) {
      setShowLocalEditNotice(false);
      return undefined;
    }
    setShowLocalEditNotice(false);
    const t = setTimeout(() => setShowLocalEditNotice(true), 10000);
    return () => clearTimeout(t);
  }, [conversationId, localEditNoticeVariant]);

  const dismissLocalEditNotice = () => {
    markLocalEditNoticeDismissed(conversationId);
    setShowLocalEditNotice(false);
  };

  // 레이아웃 안내(2단 학습지 등) — 문서가 바뀌면 다시 보인다.
  const [layoutNoticeDismissedFor, setLayoutNoticeDismissedFor] = useState(null);
  const showLayoutNotice = Boolean(layoutNotice?.title) && layoutNoticeDismissedFor !== fileId;

  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const onStageRef = useRef(onStage);
  const onPotentialEditRef = useRef(onPotentialEdit);
  const onServerSaveStartRef = useRef(onServerSaveStart);
  const onServerSaveRef = useRef(onServerSave);
  const onServerSaveFailedRef = useRef(onServerSaveFailed);
  onErrorRef.current = onError;
  onReadyRef.current = onReady;
  onStageRef.current = onStage;
  onPotentialEditRef.current = onPotentialEdit;
  onServerSaveStartRef.current = onServerSaveStart;
  onServerSaveRef.current = onServerSave;
  onServerSaveFailedRef.current = onServerSaveFailed;
  const potentialEditNotifiedRef = useRef(false);
  const iframeFocusPendingRef = useRef(false);
  const baselineExportRef = useRef(null);
  // 마지막 바이트 비교 결과와, 그 뒤 아직 비교하지 않은 편집 동작이 있었는지.
  const knownDirtyRef = useRef(false);
  const editActivityRef = useRef(false);
  const editActivityGenerationRef = useRef(0);

  // ── 단계 타이밍 + 실패 단계 계측 ────────────────────────────────────
  // onError(err, detail) / onReady(detail) 에 어느 단계에서 얼마나 걸렸는지 실어 준다.
  //   stage: create_editor | handshake | wasm_init | raw_bytes | load_file | page_count_zero
  //   wasmMs: createEditor 시작 → editorReady (iframe·JS·wasm 초기화, 재시도 포함)
  //   rawBytesMs: raw-bytes 프리페치 시작 → 바이트 검증 완료
  //   parseMs: loadFile 호출 → pageCount>0 (pageCount=0 재로드 포함)
  const timingsRef = useRef({ wasmMs: null, rawBytesMs: null, parseMs: null });
  // 진행 단계 알림(선택): create_editor → editor_ready / raw_bytes_ready → load_file. 부모가 "느림"
  // 시점에 어느 단계에 머물러 있는지 계측할 수 있게 한다. best-effort, 실패해도 흐름을 막지 않는다.
  const emitStage = useCallback((stage) => {
    try {
      onStageRef.current?.(stage, { ...timingsRef.current });
    } catch {}
  }, []);
  const emitError = useCallback((err, stage) => {
    const detail = { stage, ...timingsRef.current };
    try {
      if (err && typeof err === 'object') {
        err.stage = stage;
        err.timings = { ...timingsRef.current };
      }
    } catch {}
    if (onErrorRef.current) onErrorRef.current(err, detail);
  }, []);

  // raw-bytes 프리페치 항목: { key, promise, controller }. loadFile effect 가 key 가 같으면 재사용.
  const rawBytesPrefetchRef = useRef(null);
  const startRawBytesPrefetch = useCallback((key, { fileId: id, rawBytesPath: path }) => {
    const controller = new AbortController();
    const startedAt = _nowMs();
    const promise = fetchRhwpRawBytes({
      fileId: id,
      rawBytesPath: path,
      signal: controller.signal,
      onRetry: (attempt, max) => {
        console.warn(`[RhwpEditorViewer] raw-bytes 재시도 ${attempt}/${max} (file_id=${id})`);
        if (rawBytesPrefetchRef.current?.key === key) {
          setLoadingMessage(`잠시만요, 다시 시도하고 있어요 (${attempt + 1}/${max + 1})`);
        }
      },
    }).then((result) => {
      const rawBytesMs = Math.round(_nowMs() - startedAt);
      if (rawBytesPrefetchRef.current?.key === key) {
        timingsRef.current.rawBytesMs = rawBytesMs;
        emitStage('raw_bytes_ready');
      }
      return { ...result, rawBytesMs };
    });
    // 소비자가 아직 없거나(언마운트·교체) 나중에 await 하므로 unhandled rejection 을 막는다.
    promise.catch(() => {});
    const entry = { key, promise, controller };
    rawBytesPrefetchRef.current = entry;
    return entry;
  }, [emitStage]);

  // ── retry trigger ──────────────────────────────────────────────────────
  // 사용자가 "다시 시도" 누르면 reloadKey 증가 → loadFile effect 재실행.
  // editor 인스턴스는 그대로 유지하므로 wasm 4.6MB 재다운로드 없이 fetch+loadFile만 다시.
  const [reloadKey, setReloadKey] = useState(0);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const wasmResetCountRef = useRef(0);
  const legacyTransportResetRef = useRef(0);
  const capabilitiesRef = useRef('unknown');
  const handleRetryLoad = () => {
    setError(null);
    setReloadKey((k) => k + 1);
  };
  const handleRetry = () => {
    setError(null);
    if (editorReady) {
      setReloadKey((k) => k + 1);
      return;
    }
    setLoading(true);
    setLoadingMessage('편집기를 새로 준비하는 중이에요');
    setEditorResetKey((k) => k + 1);
  };

  const resetEditorForWasmError = useCallback((err) => {
    if (wasmResetCountRef.current >= 2) return false;
    wasmResetCountRef.current += 1;
    console.warn(
      `[RhwpEditorViewer] WASM 초기화 오류 감지 — iframe 재생성 (${wasmResetCountRef.current}/2):`,
      err
    );
    try { editorRef.current?.destroy?.(); } catch {}
    editorRef.current = null;
    try { containerRef.current?.replaceChildren?.(); } catch {}
    setEditorReady(false);
    setLoading(true);
    setLoadingMessage('편집기를 새로 준비하는 중이에요');
    setEditorResetKey((k) => k + 1);
    return true;
  }, []);

  // ── editor lifecycle — mount/unmount 시 1회 ──────────────────────────
  //
  // React 18 Strict Mode 는 dev 환경에서 effect 를 mount→unmount→mount 로 두 번
  // 실행한다. @rhwp/editor 의 createEditor 는 호출 즉시 컨테이너에 iframe 을
  // appendChild 하므로, race 가드 없이 두 번 실행되면 한 컨테이너에 iframe 이
  // 두 개 stacking 되어 위/아래로 분리된 화면이 보인다 (위는 빈/비활성, 아래만 정상).
  //
  // 가드 패턴:
  //   - closure 안 myEditor 로 본 effect 가 만든 인스턴스를 추적
  //   - await 끝났는데 disposed=true 면 즉시 destroy → iframe 도 .remove() 됨
  //   - cleanup 에서 동기적으로도 destroy (이미 await 끝났을 때 대비)
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const container = containerRef.current;
    let disposed = false;
    let myEditor = null;
    const editorStartedAt = _nowMs();

    setEditorReady(false);
    emitStage('create_editor');
    void disableRhwpStudioAutosave();

    (async () => {
      // createEditor 재시도 루프:
      //   - 총 시도 = 1 + CREATE_EDITOR_RETRY_BACKOFFS_MS.length
      //   - 첫 시도 실패 → 백오프 후 재시도. 마지막 시도까지 실패해야 onError.
      //   - timeout 류 에러는 같은 요청을 자동 반복하지 않고 재시도 UI를 노출.
      const maxAttempts = 1 + CREATE_EDITOR_RETRY_BACKOFFS_MS.length;
      let lastErr = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          try { container.replaceChildren(); } catch {}
          const studioUrl = _buildRhwpStudioUrl(attempt, editorResetKey);
          myEditor = await createEditor(
            container,
            buildRhwpCreateEditorOptions({ studioUrl }),
          );
          if (disposed) {
            // Strict Mode race: 이미 unmount 됐다 → iframe 이 DOM 에 들어갔어도 즉시 제거.
            try { myEditor.destroy(); } catch {}
            return;
          }
          const capability = readRhwpNotifySavedCapability(myEditor);
          capabilitiesRef.current = capability;
          if (capability === false && legacyTransportResetRef.current < 1) {
            legacyTransportResetRef.current += 1;
            console.info(
              '[RhwpEditorViewer] legacy transport detected; recreating iframe once'
            );
            try { myEditor.destroy(); } catch {}
            myEditor = null;
            editorRef.current = null;
            setEditorResetKey((k) => k + 1);
            return;
          }
          editorRef.current = myEditor;
          timingsRef.current.wasmMs = Math.round(_nowMs() - editorStartedAt);
          setEditorReady(true);
          emitStage('editor_ready');
          return;
        } catch (err) {
          if (disposed) return;
          try { container.replaceChildren(); } catch {}
          const isTimeout = /timeout/i.test(err.message || '');
          if (isTimeout) {
            console.warn(
              `[RhwpEditorViewer] createEditor timeout (시도 ${attempt + 1}/${maxAttempts}, ${RHWP_REQUEST_TIMEOUT_MS}ms)`
            );
            setError('편집기를 준비하는 데 시간이 오래 걸렸어요. 다시 시도해주세요.');
            setLoading(false);
            return;
          }
          lastErr = err;
          const hasMoreAttempts = attempt + 1 < maxAttempts;
          if (hasMoreAttempts) {
            const backoff = CREATE_EDITOR_RETRY_BACKOFFS_MS[attempt];
            console.warn(
              `[RhwpEditorViewer] createEditor 실패 — ${backoff}ms 후 재시도 (${attempt + 2}/${maxAttempts}): ${err.message}`
            );
            setLoadingMessage(`잠시만요, 다시 시도하고 있어요 (${attempt + 2}/${maxAttempts})`);
            await new Promise((r) => setTimeout(r, backoff));
            if (disposed) return;
            continue;
          }
          // 모든 재시도 실패 → onError → 부모가 PDF fallback 결정.
          console.error('[RhwpEditorViewer] createEditor error (모든 재시도 실패):', lastErr);
          setError('편집기 초기화 실패: ' + (lastErr.message || String(lastErr)));
          setLoading(false);
          emitError(
            lastErr,
            /handshake/i.test(lastErr?.message || '')
              ? 'handshake'
              : (_isRhwpWasmInitError(lastErr) ? 'wasm_init' : 'create_editor'),
          );
          return;
        }
      }
    })();

    return () => {
      disposed = true;
      if (myEditor) {
        try { myEditor.destroy(); } catch (e) {
          console.warn('[RhwpEditorViewer] destroy warning:', e);
        }
      }
      if (editorRef.current === myEditor) {
        editorRef.current = null;
      }
    };
  }, [editorResetKey, emitError, emitStage]);

  // ── raw-bytes 프리페치 — fileId/rawBytesPath 가 정해지는 즉시 시작 ─────
  //
  // 위 editor lifecycle(iframe·JS·wasm 초기화)과 병렬로 돈다. reloadKey(다시 시도)가
  // 바뀌거나 fileId 가 바뀌면 이전 fetch 는 abort 하고 새로 받는다. 언마운트도 abort.
  // loadFile effect 보다 먼저 선언되어야 같은 커밋에서 ref 가 먼저 채워진다.
  useEffect(() => {
    if (!fileId) return undefined;
    const key = buildRhwpRawBytesPrefetchKey(fileId, rawBytesPath, reloadKey);
    timingsRef.current.rawBytesMs = null;
    timingsRef.current.parseMs = null;
    const entry = startRawBytesPrefetch(key, { fileId, rawBytesPath });
    return () => {
      try { entry.controller.abort(); } catch {}
      if (rawBytesPrefetchRef.current === entry) rawBytesPrefetchRef.current = null;
    };
  }, [fileId, rawBytesPath, reloadKey, startRawBytesPrefetch]);

  // ── fileId 변경 → loadFile 만 호출 ────────────────────────────────────
  //
  // editor 인스턴스는 이미 위 lifecycle effect 가 만들어 두었다. 여기서는
  // 프리페치된 raw-bytes 를 기다려 editor.loadFile() 만 호출. iframe 자체는 재마운트되지 않음.
  useEffect(() => {
    if (!editorReady || !fileId) return undefined;
    const myReqId = ++reqIdRef.current;
    let disposed = false;
    setLoading(true);
    setLoadingMessage('문서 준비 중이에요');
    setError(null);
    setLoadedDocumentKind(null);
    loadedFileIdRef.current = null;
    potentialEditNotifiedRef.current = false;
    knownDirtyRef.current = false;
    editActivityRef.current = false;

    (async () => {
      // raw-bytes 는 프리페치(fetchRhwpRawBytes)에서 이미 retry 적용. loadFile 단계만 추가 retry.
      let buf;
      let detectedKind = null;
      try {
        const editor = editorRef.current;
        if (!editor) throw new Error('editor not ready');

        const key = buildRhwpRawBytesPrefetchKey(fileId, rawBytesPath, reloadKey);
        let entry = rawBytesPrefetchRef.current;
        if (!entry || entry.key !== key) {
          // 프리페치가 없거나(예: 이전 fetch 가 정리됨) 키가 다르면 지금 시작한다.
          entry = startRawBytesPrefetch(key, { fileId, rawBytesPath });
        }
        const fetched = await entry.promise;
        if (disposed || reqIdRef.current !== myReqId) return;
        buf = fetched.buf;
        detectedKind = fetched.detectedKind;
        emitStage('load_file');
      } catch (err) {
        if (disposed || reqIdRef.current !== myReqId) return;
        // 새 요청(fileId/reloadKey 변경)으로 대체되어 abort 된 경우 — 조용히 종료.
        if (err?.name === 'AbortError') return;
        console.error('[RhwpEditorViewer] raw-bytes 단계 실패:', err);
        setError(err.message || '문서 로드 실패');
        emitError(err, 'raw_bytes');
        if (!disposed && reqIdRef.current === myReqId) setLoading(false);
        return;
      }

      // loadFile 재시도 루프 — timeout 외 에러는 백오프 후 재시도.
      // timeout 은 silent (HWPX 비표준 다이얼로그 응답 대기 추정 — 부모 PDF fallback 차단).
      const maxAttempts = 1 + LOADFILE_RETRY_BACKOFFS_MS.length;
      let lastErr = null;
      let lastStage = 'load_file';
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          if (disposed || reqIdRef.current !== myReqId) return;
          const editor = editorRef.current;
          if (!editor) throw new Error('editor not ready');
          const sourceExt = detectedKind === 'hwpx' ? 'hwpx' : 'hwp';
          const sourceName = /\.(?:hwp|hwpx)$/i.test(filename || '')
            ? filename
            : `${filename || 'document'}.${sourceExt}`;
          const parseStartedAt = _nowMs();

          let result = await editor.loadFile(
            new Uint8Array(buf),
            sourceName,
            LOAD_FILE_OPTIONS
          );
          if (disposed || reqIdRef.current !== myReqId) return;

          // pageCount=0 race 가드 (Phase 3): rhwp 내부 wasm 워밍업이 덜 끝난
          // 직후 첫 loadFile 호출에서 0이 나오는 경우가 있음. 즉석에서 1.5s sleep
          // 후 같은 buffer 로 1회 재로드 (retry loop 의 다음 attempt 로 넘기지
          // 않고 같은 시도 안에서 처리 — 더 빠르고 백오프 누적 방지).
          if (!result || result.pageCount === 0) {
            console.warn(
              `[RhwpEditorViewer] pageCount=0 (시도 ${attempt + 1}) — 1.5s 후 같은 buffer 로 1회 재로드`
            );
            if (!disposed && reqIdRef.current === myReqId) {
              setLoadingMessage('양식을 다시 확인하는 중이에요');
            }
            await new Promise((r) => setTimeout(r, 1500));
            if (disposed || reqIdRef.current !== myReqId) return;
            result = await editor.loadFile(
              new Uint8Array(buf),
              sourceName,
              LOAD_FILE_OPTIONS
            );
            if (disposed || reqIdRef.current !== myReqId) return;
          }

          // 그래도 0이면 명시적 에러 — iframe 빈 화면 silent 통과 방지.
          if (!result || result.pageCount === 0) {
            const zeroErr = new Error('이 양식은 편집기가 열지 못했어요. 다른 양식으로 시도해주세요');
            zeroErr.rhwpStage = 'page_count_zero';
            throw zeroErr;
          }

          timingsRef.current.parseMs = Math.round(_nowMs() - parseStartedAt);
          if (onReadyRef.current) {
            onReadyRef.current({ pages: result.pageCount, ...timingsRef.current });
          }
          try {
            const baselineMethod = detectedKind === 'hwpx'
              ? 'exportHwpx'
              : 'exportHwp';
            if (typeof editor[baselineMethod] === 'function') {
              const baseline = await editor[baselineMethod]();
              baselineExportRef.current = baseline
                ? new Uint8Array(baseline)
                : null;
            }
          } catch {
            baselineExportRef.current = null;
          }
          wasmResetCountRef.current = 0;
          hasLoadedOnceRef.current = true;
          loadedFileIdRef.current = fileId;
          if (!disposed && reqIdRef.current === myReqId) {
            setLoadedDocumentKind(detectedKind);
            setLoading(false);
          }
          return;
        } catch (err) {
          if (disposed || reqIdRef.current !== myReqId) return;
          const isTimeout = /timeout/i.test(err.message || '');
          lastStage = err?.rhwpStage === 'page_count_zero'
            ? 'page_count_zero'
            : (_isRhwpWasmInitError(err) ? 'wasm_init' : 'load_file');
          if (_isRhwpWasmInitError(err) && resetEditorForWasmError(err)) {
            return;
          }
          if (isTimeout) {
            // 0.8.2는 loadFile 안내창을 suppress하고 60초를 기다린다. 여기까지
            // 왔다면 실제 장문서 지연/worker stall로 보고 조용히 삼키지 않는다.
            console.warn(
              `[RhwpEditorViewer] loadFile timeout (시도 ${attempt + 1}/${maxAttempts}, ${RHWP_REQUEST_TIMEOUT_MS}ms)`
            );
            if (!disposed && reqIdRef.current === myReqId) {
              setLoading(false);
              if (hasLoadedOnceRef.current) {
                setToast({
                  kind: 'error',
                  text: '새 수정본을 아직 불러오지 못했어요',
                  action: { label: '다시 불러오기', onClick: handleRetryLoad },
                });
              } else {
                setError('양식을 여는 데 시간이 오래 걸렸어요. 다시 시도해주세요.');
              }
            }
            return;
          }
          lastErr = err;
          const hasMoreAttempts = attempt + 1 < maxAttempts;
          if (hasMoreAttempts) {
            const backoff = LOADFILE_RETRY_BACKOFFS_MS[attempt];
            console.warn(
              `[RhwpEditorViewer] loadFile 실패 — ${backoff}ms 후 재시도 (${attempt + 2}/${maxAttempts}): ${err.message}`
            );
            if (!disposed && reqIdRef.current === myReqId) {
              setLoadingMessage(`잠시만요, 다시 시도하고 있어요 (${attempt + 2}/${maxAttempts})`);
            }
            await new Promise((r) => setTimeout(r, backoff));
            if (disposed || reqIdRef.current !== myReqId) return;
            continue;
          }
          // 모든 재시도 실패 → onError → 부모가 PDF fallback 결정.
          console.error('[RhwpEditorViewer] loadFile error (모든 재시도 실패):', lastErr);
          setError(lastErr.message || '문서 로드 실패');
          emitError(lastErr, lastStage);
          if (!disposed && reqIdRef.current === myReqId) setLoading(false);
          return;
        }
      }
    })();

    return () => {
      disposed = true;
    };
  }, [
    editorReady,
    fileId,
    filename,
    rawBytesPath,
    reloadKey,
    resetEditorForWasmError,
    emitError,
    emitStage,
    startRawBytesPrefetch,
  ]);

  const inspectCurrentDirty = useCallback(async () => {
    const editor = editorRef.current;
    const kind = loadedDocumentKind;
    const method = kind === 'hwpx' ? 'exportHwpx' : 'exportHwp';
    if (!editor || !kind || typeof editor[method] !== 'function') {
      return { dirty: false, checkFailed: true };
    }
    const checkedFileId = loadedFileIdRef.current;
    const activityGeneration = editActivityGenerationRef.current;
    try {
      const current = new Uint8Array(await editor[method]());
      const dirty = !sameExportBytes(current, baselineExportRef.current);
      if (loadedFileIdRef.current === checkedFileId) {
        knownDirtyRef.current = dirty;
        if (editActivityGenerationRef.current === activityGeneration) {
          editActivityRef.current = false;
        }
      }
      if (dirty && !potentialEditNotifiedRef.current) {
        potentialEditNotifiedRef.current = true;
        onPotentialEditRef.current?.();
      }
      return { dirty, checkFailed: false };
    } catch {
      return { dirty: false, checkFailed: true };
    }
  }, [loadedDocumentKind]);

  // handleDownload 는 아래에서 정의되므로 ref 로 최신 함수를 들고 있다가 imperative handle 에서 호출한다.
  const handleDownloadRef = useRef(null);
  useImperativeHandle(
    forwardedRef,
    () => ({
      flushDirtyState: inspectCurrentDirty,
      // 동기 판정(이동 차단·beforeunload 용). 아직 비교 전인 편집 동작도 포함한다.
      hasUnsavedEdits: () => Boolean(
        loadedFileIdRef.current
        && (knownDirtyRef.current || editActivityRef.current)
      ),
      // 부모가 새 파일로 교체하기 전에 "직접 고친 파일 내려받기"를 대신 눌러줄 때 쓴다.
      downloadCurrent: async () => {
        if (typeof handleDownloadRef.current !== 'function') return false;
        return handleDownloadRef.current();
      },
    }),
    [inspectCurrentDirty],
  );

  // 같은 출처 iframe 이면 편집 동작을 직접 듣고, 잠깐 멈춘 뒤 바이트 비교로 확정한다.
  // 도구막대 클릭(굵게 등)도 문서를 바꾸므로 클릭도 편집 동작으로 본다. 비교가 끝나면
  // 클릭·스크롤만 한 경우는 다시 깨끗한 상태가 된다.
  useEffect(() => {
    if (!editorReady || !loadedDocumentKind) return undefined;
    const iframe = containerRef.current?.querySelector?.('iframe');
    let iframeDocument = null;
    try {
      iframeDocument = iframe?.contentDocument || null;
    } catch {
      iframeDocument = null;
    }
    if (!iframeDocument) return undefined;
    let timer = null;
    const handleEditActivity = () => {
      if (knownDirtyRef.current) return;
      editActivityRef.current = true;
      editActivityGenerationRef.current += 1;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void inspectCurrentDirty();
      }, EDIT_ACTIVITY_SETTLE_MS);
    };
    EDIT_ACTIVITY_EVENTS.forEach((type) => {
      iframeDocument.addEventListener(type, handleEditActivity, true);
    });
    return () => {
      if (timer) clearTimeout(timer);
      EDIT_ACTIVITY_EVENTS.forEach((type) => {
        try { iframeDocument.removeEventListener(type, handleEditActivity, true); } catch {}
      });
    };
  }, [editorReady, loadedDocumentKind, fileId, inspectCurrentDirty]);

  // Cross-origin iframe changes cannot be observed directly.  Record a
  // deterministic export baseline after load, then compare when focus returns
  // from the iframe. Merely clicking/scrolling the document does not mark it
  // dirty.
  useEffect(() => {
    if (!editorReady || !onPotentialEditRef.current) return undefined;
    const handleWindowBlur = () => {
      window.setTimeout(() => {
        const iframe = containerRef.current?.querySelector?.('iframe');
        if (iframe && document.activeElement === iframe) {
          iframeFocusPendingRef.current = true;
        }
      }, 0);
    };
    const handleWindowFocus = async () => {
      if (
        !iframeFocusPendingRef.current
        || potentialEditNotifiedRef.current
      ) return;
      iframeFocusPendingRef.current = false;
      await inspectCurrentDirty();
    };
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [editorReady, fileId, inspectCurrentDirty]);

  // 토스트 자동 소멸 — 눌러야 할 버튼(다시 시도·짚어 준 버튼)이 있거나 실패 안내면 읽고 누를 시간을 더 준다.
  useEffect(() => {
    if (!toast) return undefined;
    const delay = toast.action || toast.suggest ? 10000 : toast.kind === 'error' ? 8000 : 3000;
    const t = setTimeout(() => setToast(null), delay);
    return () => clearTimeout(t);
  }, [toast]);

  // HWP/HWPX 자체 편집 → PDF 다운로드 핸들러.
  //
  // 흐름: wasm.exportHwpx/exportHwp() → backend export-edited-pdf (LibreOffice 변환)
  //      → .pdf 다운로드.
  //
  // 사용처: 사용자가 iframe 안에서 직접 편집한 결과를 한컴 저장 안정성 문제와
  // 무관하게 안전한 PDF 산출물로 받고 싶을 때. PDF 는 재편집 불가.
  const handleDownloadPdf = async () => {
    if (downloadingPdf || !fileId) return;
    // 다른 저장 버튼과 같은 권위를 쓴다: previewKind(서버 힌트)가 아니라 실제 로드한 바이트 magic(loadedDocumentKind).
    // 파일 교체 직후 이전 문서를 새 이름으로 PDF 저장하는 race 도 loadedFileIdRef 로 막는다.
    if (
      !editorRef.current
      || !loadedDocumentKind
      || loadedFileIdRef.current !== fileId
    ) {
      setToast({ kind: 'error', text: '문서를 아직 준비하고 있어요. 잠시 후 다시 시도해주세요' });
      return;
    }
    const exportFn = loadedDocumentKind === 'hwp_v5' ? 'exportHwp' : 'exportHwpx';
    if (typeof editorRef.current[exportFn] !== 'function') {
      setToast({ kind: 'error', text: '잠시 후 다시 시도해주세요' });
      return;
    }
    setDownloadingPdf(true);
    setToast(null);
    try {
      const bytes = await editorRef.current[exportFn]();
      if (!bytes || bytes.length === 0) {
        throw new Error('편집본을 만들지 못했어요.');
      }
      if (bytes.length > RHWP_MAX_UPLOAD_BYTES) {
        const error = new Error('edited document exceeds upload limit');
        error.userMessage = RHWP_PDF_TOO_LARGE_MESSAGE;
        throw error;
      }

      // export-edited-pdf 는 LibreOffice 변환 비용이 큼. 재시도는 보수적으로
      // (총 3회, 60s/회). 사용자가 명시적으로 누른 다운로드라 timeout 후 재시도는 비활성.
      const res = await fetchWithRetry(
        `${API_BASE_URL}/api/chat/files/${encodeURIComponent(fileId)}/export-edited-pdf`,
        {
          method: 'POST',
          body: bytes,
          headers: await getAuthHeaders({ 'Content-Type': 'application/octet-stream' }),
        },
        EXPORT_PDF_FETCH_OPTS
      );
      if (!res.ok) {
        let detail = `PDF 변환 실패 (${res.status})`;
        try { const j = await res.json(); if (j && j.detail) detail = j.detail; } catch {}
        const error = new Error(detail);
        if (res.status === 413 || res.status === 422) error.userMessage = detail;
        throw error;
      }
      const buf = await res.arrayBuffer();
      if (buf.byteLength === 0) throw new Error('PDF 응답이 비어있습니다.');

      const blob = new Blob([buf], { type: 'application/pdf' });
      const stem = (filename || '양식').replace(/\.(hwp|hwpx)$/i, '');
      if (!downloadBlobFile(blob, `${stem} (편집됨).pdf`)) {
        throw new Error('브라우저에서 PDF 저장을 시작하지 못했어요.');
      }
      setToast({ kind: 'success', text: 'PDF를 저장했어요' });
      onDownload?.({ entry: 'editor_pdf', format: 'pdf', ok: true });
    } catch (err) {
      console.error('[RhwpEditorViewer] pdf download error:', err);
      onDownload?.({ entry: 'editor_pdf', format: 'pdf', ok: false, failureStage: 'export_pdf' });
      setToast({
        kind: 'error',
        text: err?.userMessage || 'PDF로 저장하지 못했어요. 잠시 후 다시 시도해주세요',
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // 편집본 저장은 rhwp-studio 0.8.x의 출처 포맷 보존 정책과 맞춘다.
  //   - HWPX 원본: exportHwpx() → .hwpx (불필요한 HWP 변환 및 오탐 차단 방지)
  //   - HWP v5 원본: exportHwpVerify() → exportHwp() → .hwp
  // previewKind가 아니라 실제로 로드한 raw-bytes magic을 권위로 사용한다.
  const handleDownload = async () => {
    if (downloading || !fileId) return false;
    setDownloading(true);
    setToast(null);
    let failureStage = 'readiness';
    let exportedBytesLen = null;
    let verifyTelemetry = { verify_status: 'skipped' };
    const telemetryKind =
      loadedDocumentKind === 'hwp_v5' || loadedDocumentKind === 'hwpx'
        ? loadedDocumentKind
        : 'unknown';
    try {
      const editor = editorRef.current;
      if (
        !editor
        || !loadedDocumentKind
        || loadedFileIdRef.current !== fileId
      ) {
        throw new Error('편집기가 아직 준비되지 않았습니다.');
      }
      const isHwpxSource = loadedDocumentKind === 'hwpx';
      const exportMethod = isHwpxSource ? 'exportHwpx' : 'exportHwp';
      if (typeof editor[exportMethod] !== 'function') {
        throw new Error('이 한글 파일의 저장 기능을 준비하지 못했어요.');
      }

      // HWP v5만 HWP serializer 자기검증을 적용한다. HWPX에 이 검증을 적용하면
      // 저장하려는 HWPX가 아니라 별도 HWP 변환 결과의 페이지 수를 검사하게 된다.
      // ★ 0.8.x exportHwpVerify() 자기검증 (0.7.18에서 도입) — 직렬화 결과를 wasm 이
      //   스스로 재로드해 pageCount 를 비교한다. recovered=false / 페이지 수
      //   불일치 / 빈 결과면 깨진 파일이므로 사용자 PC 저장 전에 차단하고
      //   PDF 저장을 유도한다.
      //   verify 호출 자체가 실패(iframe 통신 등)하면 오탐 차단이 더 큰 피해라
      //   기존 경로로 관대하게 진행한다 — 명시적 검증 실패만 막는다.
      failureStage = 'verify';
      if (!isHwpxSource && typeof editor.exportHwpVerify === 'function') {
        let verify = null;
        try {
          verify = await editor.exportHwpVerify();
        } catch (verifyErr) {
          verifyTelemetry = { verify_status: 'unavailable' };
          console.warn('[RhwpEditorViewer] exportHwpVerify 호출 실패 — 검증 없이 진행:', verifyErr);
        }
        if (verify) {
          const pagesMatch =
            Number(verify.pageCountAfter) === Number(verify.pageCountBefore);
          verifyTelemetry = {
            verify_status:
              verify.recovered && pagesMatch && verify.bytesLen
                ? 'passed'
                : 'failed',
            recovered: Boolean(verify.recovered),
            page_count_before: Number(verify.pageCountBefore),
            page_count_after: Number(verify.pageCountAfter),
            bytes_len: Number(verify.bytesLen),
          };
          if (!verify.recovered || !pagesMatch || !verify.bytesLen) {
            console.error('[RhwpEditorViewer] HWP 저장 자기검증 실패 — 다운로드 차단:', verify);
            throw new Error(
              '편집 내용을 한글 파일로 안전하게 저장하지 못할 것 같아요. PDF로 저장해주세요.'
            );
          }
          console.log(
            `[RhwpEditorViewer] HWP 저장 자기검증 통과: ${verify.bytesLen}B, ` +
            `pages ${verify.pageCountBefore}→${verify.pageCountAfter}`
          );
        }
      }

      failureStage = 'export';
      const bytes = await editor[exportMethod]();
      if (!bytes || bytes.length === 0) {
        throw new Error('편집본을 만들지 못했어요. 잠시 후 다시 시도해 주세요.');
      }
      const exportedKind = detectRhwpDocumentKind(bytes);
      if (exportedKind !== loadedDocumentKind) {
        throw new Error(
          isHwpxSource
            ? '편집한 HWPX 파일을 만들지 못했어요. 채팅의 원본 파일은 그대로 받을 수 있어요.'
            : '편집한 HWP 파일 형식을 확인하지 못했어요. PDF로 저장해주세요.'
        );
      }
      exportedBytesLen = bytes.length;
      const downloadBuf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
      const extension = isHwpxSource ? 'hwpx' : 'hwp';
      const mimeType = isHwpxSource
        ? 'application/vnd.hancom.hwpx'
        : 'application/x-hwp';
      const blob = new Blob([downloadBuf], { type: mimeType });
      const stem = (filename || '양식').replace(/\.(hwp|hwpx)$/i, '');
      const downloadName = `${stem} (편집됨).${extension}`;
      failureStage = 'browser_download';
      if (!downloadBlobFile(blob, downloadName)) {
        throw new Error('브라우저에서 파일 저장을 시작하지 못했어요. 다시 시도해주세요.');
      }

      // 브라우저에 파일을 넘긴 뒤 dirty 상태와 자동복구 draft를 정리한다.
      // 통지 실패는 이미 시작된 다운로드를 실패로 바꾸지 않는다.
      if (typeof editor.notifySaved === 'function') {
        try {
          await editor.notifySaved(downloadName);
        } catch (notifyError) {
          console.warn('[RhwpEditorViewer] 저장 완료 통지 생략:', notifyError);
        }
      }
      // 작업 저장이 따로 있는 화면은 PC로 받아도 왓퀴즈에는 아직 저장 전이다. 수정 상태를 지우면
      // 기록 저장이 직접 고친 내용 없이 지나간다.
      const unsavedOnServer = Boolean(serverSavePath)
        && !sameExportBytes(new Uint8Array(bytes), baselineExportRef.current);
      if (loadedFileIdRef.current === fileId && !serverSavePath) {
        baselineExportRef.current = new Uint8Array(bytes);
        knownDirtyRef.current = false;
        editActivityRef.current = false;
        potentialEditNotifiedRef.current = false;
      }
      void reportHwpExportTelemetry(fileId, {
        event: 'download_success',
        doc_kind: telemetryKind,
        ...verifyTelemetry,
        bytes_len: exportedBytesLen,
      }, telemetryPath);
      setToast(unsavedOnServer
        ? {
          kind: 'success',
          text: '내 컴퓨터에 받았어요. 왓퀴즈에도 남기려면 작업 저장을 눌러주세요',
          suggest: 'server-save',
        }
        : { kind: 'success', text: '한글 파일로 저장했어요' });
      onDownload?.({ entry: 'editor_hwp', format: extension, ok: true });
      return true;
    } catch (err) {
      console.error('[RhwpEditorViewer] download error:', err);
      onDownload?.({ entry: 'editor_hwp', format: telemetryKind === 'hwp_v5' ? 'hwp' : 'hwpx', ok: false, failureStage });
      void reportHwpExportTelemetry(fileId, {
        event: 'download_failed',
        doc_kind: telemetryKind,
        failure_stage: failureStage,
        ...verifyTelemetry,
        bytes_len: exportedBytesLen ?? verifyTelemetry.bytes_len,
      }, telemetryPath);
      setToast({
        kind: 'error',
        text: err.message || (
          loadedDocumentKind === 'hwpx'
            ? '편집한 HWPX 파일을 저장하지 못했어요. 잠시 후 다시 시도해주세요.'
            : '저장하지 못했어요. PDF로 저장해주세요'
        ),
      });
      return false;
    } finally {
      setDownloading(false);
    }
  };
  handleDownloadRef.current = handleDownload;

  const handleServerSave = async () => {
    if (savingServer || !fileId || !serverSavePath) return;
    setSavingServer(true);
    setToast(null);
    onServerSaveStartRef.current?.();
    try {
      const editor = editorRef.current;
      if (
        !editor
        || !loadedDocumentKind
        || loadedFileIdRef.current !== fileId
      ) {
        throw serverSaveError('한글 문서를 아직 여는 중이에요. 잠시 후 다시 눌러주세요.', 'not_ready', 'retry');
      }
      const isHwpxSource = loadedDocumentKind === 'hwpx';
      const exportMethod = isHwpxSource ? 'exportHwpx' : 'exportHwp';
      if (typeof editor[exportMethod] !== 'function') {
        throw serverSaveError(
          "이 한글 파일은 작업 저장을 할 수 없어요. 지금 화면은 'PDF로 저장'으로 받아 두세요.",
          'export_unavailable',
          'pdf',
        );
      }

      // handleDownload 와 같은 정책: 명시적 검증 실패만 막고, verify 호출 자체가 실패(iframe 통신 등)하면
      // 오탐 차단이 더 큰 피해라 검증 없이 진행한다(서버가 rhwp 재열기로 다시 검증한다).
      let verify = null;
      if (!isHwpxSource && typeof editor.exportHwpVerify === 'function') {
        try {
          verify = await editor.exportHwpVerify();
        } catch (verifyErr) {
          verify = null;
          console.warn('[RhwpEditorViewer] server save exportHwpVerify 호출 실패 — 검증 없이 진행:', verifyErr);
        }
        if (verify) {
          const pagesMatch =
            Number(verify.pageCountAfter) === Number(verify.pageCountBefore);
          if (!verify.recovered || !pagesMatch || !verify.bytesLen) {
            throw serverSaveError(RHWP_SERVER_SAVE_PAGES_CHANGED_MESSAGE, 'editor_verify_failed', 'pdf');
          }
        }
      }

      const bytes = await editor[exportMethod]();
      if (!bytes || bytes.length === 0) {
        throw serverSaveError('편집한 내용을 파일로 만들지 못했어요. 잠시 후 다시 눌러주세요.', 'export_empty', 'retry');
      }
      if (detectRhwpDocumentKind(bytes) !== loadedDocumentKind) {
        throw serverSaveError(
          "편집한 한글 파일 형식이 바뀌어서 저장하지 않았어요. 지금 화면은 'PDF로 저장'으로 받아 두세요.",
          'kind_mismatch',
          'pdf',
        );
      }
      if (bytes.length > RHWP_MAX_UPLOAD_BYTES) {
        throw serverSaveError(RHWP_SAVE_TOO_LARGE_MESSAGE, 'too_large', 'download');
      }
      const saveUrl = resolveRhwpServerSaveUrl(fileId, serverSavePath);
      const headers = await getAuthHeaders({
        'Content-Type': 'application/octet-stream',
      });
      if (verify) {
        headers['X-Rhwp-Verify'] = encodeURIComponent(JSON.stringify(verify));
      }
      let response;
      try {
        response = await fetchWithRetry(
          saveUrl,
          {
            method: 'PUT',
            headers,
            body: bytes,
          },
          {
            maxRetries: 1,
            timeoutMs: 120_000,
            retryOnTimeout: false,
          }
        );
      } catch (networkError) {
        console.warn('[RhwpEditorViewer] server save request failed:', networkError);
        throw serverSaveError(RHWP_SERVER_SAVE_FAILED_MESSAGE, 'network', 'retry');
      }
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        const detail = typeof body?.detail === 'string'
          ? body.detail
          : body?.detail?.message;
        const failure = serverSaveFailureOf(response.status, body?.detail);
        throw serverSaveError(detail || RHWP_SERVER_SAVE_FAILED_MESSAGE, failure.reason, failure.next);
      }
      if (typeof editor.notifySaved === 'function') {
        try {
          await editor.notifySaved(filename || '평가계획서');
        } catch (notifyError) {
          console.warn('[RhwpEditorViewer] 서버 저장 완료 통지 생략:', notifyError);
        }
      }
      potentialEditNotifiedRef.current = false;
      baselineExportRef.current = new Uint8Array(bytes);
      knownDirtyRef.current = false;
      editActivityRef.current = false;
      if (conversationId) {
        markLocalEditNoticeDismissed(conversationId);
        setShowLocalEditNotice(false);
      }
      const savedAt = new Date().toISOString();
      onServerSaveRef.current?.({
        ...(body || {}),
        bytesLen: bytes.length,
        kind: loadedDocumentKind,
        savedAt,
      });
      setToast({ kind: 'success', text: '작업 내용을 저장했어요' });
    } catch (err) {
      console.error('[RhwpEditorViewer] server save error:', err);
      const failure = err?.saveFailure || { reason: 'unexpected', next: 'retry' };
      onServerSaveFailedRef.current?.({ reason: failure.reason });
      let suggest = null;
      if (failure.next === 'pdf' && canDownloadPdf) suggest = 'pdf';
      else if (failure.next === 'retry') suggest = 'server-save';
      else if (failure.next === 'download') suggest = 'download';
      setToast({
        kind: 'error',
        text: err?.saveFailure ? err.message : RHWP_SERVER_SAVE_FAILED_MESSAGE,
        suggest,
      });
    } finally {
      setSavingServer(false);
    }
  };

  const canDownloadPdf = allowPdfExport && (
    previewKind === 'hwpx' || previewKind === 'hwp_v5'
  );
  const suggestedAction = toast?.suggest || null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 레이아웃 안내 — 2단 학습지처럼 패널 표시가 실제 한글 인쇄 모양과 다를 때 */}
      {showLayoutNotice && (
        <div
          role="note"
          data-testid="rhwp-layout-notice"
          style={{
            margin: '10px 12px 0',
            background: '#F2F4F6',
            borderRadius: 10,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#333D4B',
              marginBottom: 2,
              letterSpacing: '-0.01em',
            }}>
              {layoutNotice.title}
            </div>
            {layoutNotice.body && (
              <div style={{ fontSize: 12, color: '#6B7684', lineHeight: 1.55 }}>
                {layoutNotice.body}
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="안내 닫기"
            onClick={() => setLayoutNoticeDismissedFor(fileId)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#8B95A1',
              cursor: 'pointer',
              fontSize: 18,
              padding: 2,
              lineHeight: 1,
              borderRadius: 6,
            }}
          >
            ×
          </button>
        </div>
      )}
      {/* 양식 위 안내 카드 — 5초 후 fade-in, 대화 단위 dismiss. */}
      {showLocalEditNotice && (
        <div
          style={{
            margin: '10px 12px 0',
            background: '#fff',
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            flexShrink: 0,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.03)',
            animation: 'rhwpNoticeIn 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: '#3182F6',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
              marginTop: 1,
              fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
            }}
            aria-hidden="true"
          >
            i
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#191F28',
              marginBottom: 4,
              letterSpacing: '-0.01em',
            }}>
              여기서 고친 내용은 작업 저장을 눌러주세요
            </div>
            <div style={{ fontSize: 13, color: '#4E5968', lineHeight: 1.6 }}>
              문서 안에서 바로 수정할 수 있어요.<br />
              수정한 뒤 작업 저장을 누르면 현재 내용을 안전하게 보관해요.
            </div>
            {canDownloadPdf && (
              <div style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid #F2F4F6',
                fontSize: 12,
                color: '#6B7684',
                lineHeight: 1.55,
              }}>
                {previewKind === 'hwpx'
                  ? '직접 적은 내용까지 한글 파일로 저장할 수 있어요. 레이아웃 확인용으로는 PDF 저장도 가능해요.'
                  : '직접 적은 내용까지 한글 파일로 저장할 수 있어요. 열리지 않으면 PDF로 저장해주세요.'}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={dismissLocalEditNotice}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#8B95A1',
              cursor: 'pointer',
              fontSize: 20,
              padding: 4,
              marginTop: -2,
              marginRight: -4,
              lineHeight: 1,
              borderRadius: 6,
              flexShrink: 0,
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#F2F4F6';
              e.currentTarget.style.color = '#4E5968';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#8B95A1';
            }}
            aria-label="안내 닫기"
          >
            ×
          </button>
          <style>{`
            @keyframes rhwpNoticeIn {
              from { opacity: 0; transform: translateY(-6px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          background: '#F3F4F6',
        }}
      >
        <div
          ref={containerRef}
          style={{ width: '100%', height: '100%' }}
        />
        {loading && (
          <Overlay
            text={loadingMessage}
            highlight={
              previewKind === 'hwpx'
                ? '안내창이 뜨면 "그대로 보기"를 눌러주세요'
                : null
            }
          />
        )}
        {error && (
          <Overlay
            text={`양식을 여는 중에 잠시 멈췄어요`}
            kind="error"
            highlight={error}
            action={{ label: '다시 시도', onClick: handleRetry }}
          />
        )}

      </div>

      {/* 하단 고정 바 — 다운로드 / 토스트 */}
      <div
        className="rhwp-editor-actions"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          rowGap: 6,
          padding: '8px 16px',
          borderTop: '1px solid #E5E7EB',
          background: '#fff',
          flexShrink: 0,
        }}
      >
        {toast && (
          <span
            className="rhwp-editor-toast"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              flex: '1 1 0%',
              minWidth: 0,
              fontSize: 13,
              lineHeight: 1.45,
              color: toast.kind === 'error' ? '#F04452' : '#4E5968',
              fontWeight: 500,
            }}
          >
            {toast.text}
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  setToast(null);
                  toast.action.onClick();
                }}
                style={{
                  border: 'none',
                  borderRadius: 6,
                  background: '#3182F6',
                  color: '#fff',
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {toast.action.label}
              </button>
            )}
          </span>
        )}
        {!toast && <div className="rhwp-editor-action-spacer" style={{ flex: 1 }} />}
        {serverSavePath && (
          <button
            className={`rhwp-editor-action rhwp-editor-action--server-save${suggestedAction === 'server-save' ? ' rhwp-editor-action--suggested' : ''}`}
            type="button"
            onClick={handleServerSave}
            disabled={savingServer || downloadingPdf || downloading || loading || !!error}
            title="현재 한글 편집 내용을 서버에 임시 보관해요"
            style={{
              border: 'none',
              borderRadius: 8,
              background: '#3182F6',
              color: '#fff',
              minHeight: 44,
              padding: '8px 18px',
              fontSize: 14,
              fontWeight: 600,
              cursor: savingServer || loading ? 'wait' : 'pointer',
              opacity: savingServer || downloadingPdf || downloading || loading || error ? 0.5 : 1,
              boxShadow: suggestedAction === 'server-save' ? RHWP_SUGGESTED_ACTION_RING : 'none',
              transition: 'background 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            {savingServer ? '저장 중...' : serverSaveLabel}
          </button>
        )}
        {canDownloadPdf && (
          <button
            className={`rhwp-editor-action rhwp-editor-action--secondary${suggestedAction === 'pdf' ? ' rhwp-editor-action--suggested' : ''}`}
            type="button"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || downloading || savingServer || loading || !!error}
            title="화면에 보이는 내용을 PDF로 저장해요"
            style={{
              border: 'none',
              borderRadius: 8,
              background: '#F2F4F6',
              color: '#4E5968',
              minHeight: 44,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: downloadingPdf || loading ? 'wait' : 'pointer',
              opacity: downloadingPdf || downloading || savingServer || loading || error ? 0.5 : 1,
              boxShadow: suggestedAction === 'pdf' ? RHWP_SUGGESTED_ACTION_RING : 'none',
              transition: 'background 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) e.currentTarget.style.background = '#E5E8EB';
            }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#F2F4F6'; }}
          >
            {downloadingPdf ? 'PDF 만드는 중...' : 'PDF로 저장'}
          </button>
        )}
        <button
          className={`rhwp-editor-action rhwp-editor-action--download${suggestedAction === 'download' ? ' rhwp-editor-action--suggested' : ''}`}
          type="button"
          onClick={handleDownload}
          disabled={downloading || downloadingPdf || savingServer || loading || !!error}
          style={{
            border: 'none',
            borderRadius: 8,
            background: serverSavePath ? '#F2F4F6' : '#3182F6',
            color: serverSavePath ? '#4E5968' : '#fff',
            minHeight: 44,
            padding: '8px 20px',
            fontSize: 14,
            fontWeight: 600,
            cursor: downloading || loading ? 'wait' : 'pointer',
            opacity: downloading || downloadingPdf || savingServer || loading || error ? 0.5 : 1,
            boxShadow: suggestedAction === 'download' ? RHWP_SUGGESTED_ACTION_RING : 'none',
            transition: 'background 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.background = serverSavePath ? '#E5E8EB' : '#2563EB';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = serverSavePath ? '#F2F4F6' : '#3182F6';
          }}
        >
          {downloading
            ? '다운로드 준비 중...'
            : serverSavePath
            ? '파일 다운로드'
            : '한글 파일로 저장'}
        </button>
        <style>{`
          .rhwp-editor-action {
            white-space: nowrap;
          }
          @media (max-width: 480px) {
            .rhwp-editor-actions {
              flex-wrap: wrap;
              padding: 8px 10px 10px !important;
            }
            .rhwp-editor-action-spacer {
              display: none;
            }
            .rhwp-editor-action {
              flex: 1 1 calc(50% - 4px);
              min-width: 0;
              padding: 9px 8px !important;
            }
            .rhwp-editor-action--server-save {
              flex-basis: 100%;
            }
            .rhwp-editor-actions > span {
              flex-basis: 100% !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
});

export default RhwpEditorViewer;
